import React from 'react';
import classNames from 'classnames/bind';
import { Pagination } from '@mantine/core';
import { useToggle } from '@mantine/hooks';
import { CellProps, Column } from 'react-table';
import { useLocation, useNavigate } from 'react-router-dom';

import { getStreamStatusHelpText, Stream } from 'types/stream';

import { relativeTimeFromNow } from 'services/date';
import { isNotNullOrUndefined } from 'services/nullable';
import { pluralize } from 'services/string';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useAPI } from 'hooks/api/useAPI';
import { useAppRedirect } from 'hooks/useAppRedirect';
import { useHasAccess } from 'hooks/useHasAccess';
import { useModal } from 'hooks/useModal';
import { useDeleteStreams } from 'streams/hooks/useDeleteStreams';
import { useGateways } from 'gateways/hooks/useGateways';
import { useUpdateStream } from 'streams/hooks/useUpdateStream';

import { Button } from 'components/Button/Button';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { Loader } from 'components/Loader/Loader';
import {
  PaginatedTable,
  PaginatedTableProps,
} from 'components/Table/PaginatedTable';
import { TableLabelsCell } from 'components/Table/Table';
import { TableBulkActionType } from 'components/Table/components/BulkActions/BulkActions';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';
import { Text } from 'components/Text/Text';
import { TextAppLink } from 'components/TextAppLink/TextAppLink';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';
import { ViewMode } from 'components/ViewModeSwitch/ViewModeSwitch';
import { CreateInputStreamsDialog } from 'streams/components/CreateInputStreamsDialog';
import { StreamsTableActionsCell } from 'streams/components/Table/ActionsCell';
import { StreamsTableInfoCell } from 'streams/components/Table/InfoCell';

import styles from './Table.module.scss';

const c = classNames.bind(styles);

export type InputStreamsTableProps = Pick<
  PaginatedTableProps<Stream>,
  'queryResult' | 'pageSize' | 'page' | 'onPageChange'
> & {
  hasFilters: boolean;
  viewMode: ViewMode;
};

export function InputStreamsTable({
  hasFilters,
  viewMode,
  ...props
}: InputStreamsTableProps) {
  const navigate = useNavigate();
  const { mutate } = useUpdateStream();
  const { search } = useLocation();
  const { applicationID } = useAPI();
  const redirect = useAppRedirect();

  const [hasAccess] = useHasAccess();
  const { open } = useModal();

  const [isCreatingInputStream, toggleIsCreatingInputStream] = useToggle();

  const { mutateAsync: deleteStreams, isLoading: isDeletingStreams } =
    useDeleteStreams();

  const gatewayIDs = [
    ...new Set(
      props.queryResult?.data?.data
        .map(({ gateway_id }) => gateway_id)
        .filter(isNotNullOrUndefined)
    ),
  ];

  const { data: gateways } = useGateways({
    params: { gateway_ids: gatewayIDs },
    keepPreviousData: false,
  });

  const bulkActions = React.useMemo(() => {
    if (hasAccess('deploy_edit')) {
      return [
        {
          action: 'Delete',
          intent: 'danger',
          onClick: (ids) => {
            open('confirm-delete', {
              title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'stream')}?`,
              onConfirm: () => deleteStreams({ stream_ids: ids }),
            });
          },
          loading: isDeletingStreams,
        },
      ] as TableBulkActionType[];
    }
  }, [hasAccess, isDeletingStreams, open, deleteStreams]);

  const { data: currentPlan, isSuccess: isPlanSuccess } = useCurrentPlan();
  const canCreateTags =
    (isPlanSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);

  const [isRenamingStream, setIsRenamingStream] = React.useState<string>();

  const emptyMessage = hasFilters ? (
    <EmptyView title="No streams matching the filter criteria found.">
      Please try another filter combination or clear filters.
    </EmptyView>
  ) : (
    <EmptyView title="You have not added any streams yet.">
      <Button onClick={() => toggleIsCreatingInputStream()}>
        Create input stream
      </Button>
      <CreateInputStreamsDialog
        open={isCreatingInputStream}
        onClose={toggleIsCreatingInputStream}
      />
    </EmptyView>
  );

  const columns = React.useMemo(() => {
    function getStreamGateway(gatewayID: string | undefined | null) {
      return gatewayID
        ? gateways?.data.find(({ id }) => id === gatewayID)
        : undefined;
    }

    function handleRenameInputStream(stream: Stream) {
      mutate({ id: stream.id, name: stream.name });
      setIsRenamingStream(undefined);
    }

    function handleCancelRenameInputStream() {
      setIsRenamingStream(undefined);
    }

    function InfoCell(props: CellProps<Stream>) {
      return (
        <StreamsTableInfoCell
          {...props}
          isRenaming={isRenamingStream === props.row.original.id}
          gateway={getStreamGateway(props.row.original.gateway_id)}
          onCancelRename={handleCancelRenameInputStream}
          onRename={handleRenameInputStream}
          to={`/deploy/streams/${props.row.original.id}`}
          key={`stream-info_${props.row.original.id}`}
        />
      );
    }

    const columns: Column<Stream>[] = [
      {
        Header: 'Stream',
        Cell: InfoCell,
      },
      {
        Header: 'State',
        Cell({ row }: CellProps<Stream>) {
          return (
            <StatusIndicator
              status={row.original.status}
              tooltip={getStreamStatusHelpText(row.original)}
            />
          );
        },
      },
      {
        Header: 'Gateway',
        Cell(props: CellProps<Stream>) {
          const { gateway_id } = props.row.original;

          const gateway = gateway_id
            ? gateways?.data.find(({ id }) => id === gateway_id)
            : undefined;

          if (!gateway) {
            return null;
          }

          return (
            <TextAppLink to={`/deploy/gateways/${gateway.id}`}>
              {gateway.name}
            </TextAppLink>
          );
        },
      },
    ];

    if (canCreateTags) {
      columns.push({
        Header: 'Labels',
        Cell(props: CellProps<Stream>) {
          return (
            <TableLabelsCell
              {...props}
              entity="streams"
              listQueryKey={['streams']}
              singleQueryKey={['stream']}
              readOnly={!hasAccess('deploy_edit', 'cameras')}
            />
          );
        },
      });
    }

    columns.push({
      id: 'updated_at',
      Header: 'Last Activity',
      accessor(stream: Stream) {
        return relativeTimeFromNow(stream.updated_at);
      },
      sortType: 'date',
    });

    if (hasAccess('deploy')) {
      function ActionsCell(props: CellProps<Stream>) {
        return (
          <StreamsTableActionsCell
            {...props}
            onRenameClick={setIsRenamingStream}
            key={`stream-actions_${props.row.original.id}`}
          />
        );
      }

      columns.push({
        Header: ' ',
        Cell: ActionsCell,
      });
    }

    return columns;
  }, [canCreateTags, gateways?.data, isRenamingStream, mutate, hasAccess]);

  const { isInitialLoading, isSuccess, error, data } = props.queryResult;

  function navigateToStream(stream: Stream, openInNewTab = false) {
    const to = `/deploy/streams/${stream.id + search}`;

    if (openInNewTab) {
      window.open(`/applications/${applicationID}${to}`, '_blank');
      return;
    }

    redirect(to);
  }

  function handleGridItemNavigate(
    event: React.MouseEvent<HTMLElement>,
    stream: Stream
  ) {
    if (
      event.target !== event.currentTarget &&
      (['input', 'label', 'a', 'button'].includes(
        (event.target as Element).localName
      ) ||
        !event.currentTarget.contains(event.target as HTMLElement))
    ) {
      return;
    }

    navigateToStream(stream, event.metaKey);
  }

  if (viewMode === 'grid') {
    if (isInitialLoading) {
      return (
        <div className={c('loader')}>
          <Loader size="small" text="Loading..." />
        </div>
      );
    }

    if (isSuccess && data?.total_elements === 0) {
      return emptyMessage;
    }

    if (error || !data) {
      return <Text>{error?.message || 'An unexpected error occurred.'}</Text>;
    }

    return (
      <>
        <div className={c('grid')}>
          {data.data.map((stream) => (
            <article
              className={c('grid-item')}
              key={stream.id}
              onClick={(event) => handleGridItemNavigate(event, stream)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigateToStream(stream, event.metaKey);
                }
              }}
              tabIndex={0}
              role="button"
            >
              <VideoSourceSnapshot
                className={c('grid-item-preview')}
                source={stream}
                autoRefresh={false}
                showSettingsButton={
                  hasAccess('deploy_edit') && stream.stream_type === 'rtsp'
                }
                onSettingsClick={() =>
                  navigate(
                    `/applications/${applicationID}/deploy/streams/${stream.id}`,
                    {
                      state: { tab: 'settings' },
                    }
                  )
                }
              />
            </article>
          ))}
        </div>

        {data.total_pages > 1 && (
          <div className={c('pagination')}>
            <Pagination
              total={data.total_pages}
              value={props.page ?? 1}
              onChange={props.onPageChange}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <PaginatedTable
      {...props}
      id="streams-table"
      label="streams"
      columns={columns}
      bulkActions={bulkActions}
      onRowNavigate={(stream) => `/deploy/streams/${stream.id + search}`}
      emptyMessage={emptyMessage}
    />
  );
}
